import { Localization } from "@viamap/viamap2-common";
import type { LLMAnalysisResultItem, LLMMetadata } from "src/modules/ANNModule/ANNLLMAnalyzer";
import {
   provenanceAfterDocumentChange,
   provenanceAfterPageRevert,
   type ANNServitutProvenance,
} from "src/modules/ANNModule/ANNServitutProvenance";
import type { ServitutFieldAnnotationMap } from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import { getRowProvenance } from "./ServitutRedegoerelseRow";

function looksLikeElectronicServitut(servitut: any): boolean {
   if (!servitut) {
      return false;
   }
   if (String(servitut.dokumentFilnavn || "").trim()) {
      return false;
   }
   if (String(servitut.aktAliasId || "").trim()) {
      return true;
   }
   return Boolean(String(servitut.dokumentId || "").trim() && !String(servitut.aktId || "").trim());
}

type ServitutRedegoerelseRowHandling = "legacyLlmPdf" | "electronicZipPackage";
type ServitutRedegoerelseDocumentSource = "tingbog" | "annotation" | "customUpload" | "rigsarkivetRequired";

export type ServitutAktRestoreSnapshot = {
   handling: ServitutRedegoerelseRowHandling;
   documentSource?: ServitutRedegoerelseDocumentSource;
   electronicServitut?: any;
   aktDocument?: string;
   aktId?: string;
   documentUrl?: string;
   pdfStatus: "pending" | "ready" | "missing" | "failed";
   pdfSource?: string;
   analysisItem?: LLMAnalysisResultItem;
   selectedPages?: number[];
   provenance?: ANNServitutProvenance;
   sourceMessage?: string;
   resultsAccepted?: boolean;
   fieldAnnotations?: ServitutFieldAnnotationMap;
   metadata?: LLMMetadata;
};

type RestorableRow = {
   handling?: ServitutRedegoerelseRowHandling;
   documentSource?: ServitutRedegoerelseDocumentSource;
   electronicServitut?: any;
   customPdf?: unknown;
   aktRestore?: ServitutAktRestoreSnapshot;
   annotationRecord?: unknown;
   annotationDocument?: unknown;
   aktDocument?: string;
   aktId?: string;
   documentUrl?: string;
   pdfStatus: "pending" | "ready" | "missing" | "failed";
   pdfSource?: string;
   analysisItem?: LLMAnalysisResultItem;
   selectedPages?: number[];
   provenance?: ANNServitutProvenance;
   sourceMessage?: string;
   resultsAccepted?: boolean;
   fieldAnnotations?: ServitutFieldAnnotationMap;
   servitut?: any;
};

export function captureAktRestoreSnapshot(
   row: RestorableRow,
   metadata?: LLMMetadata,
): ServitutAktRestoreSnapshot {
   return {
      handling: row.handling ?? "legacyLlmPdf",
      documentSource: row.documentSource,
      electronicServitut: row.electronicServitut,
      aktDocument: row.aktDocument,
      aktId: row.aktId,
      documentUrl: row.documentUrl,
      pdfStatus: row.pdfStatus,
      pdfSource: row.pdfSource,
      analysisItem: row.analysisItem,
      selectedPages: row.selectedPages,
      provenance: row.provenance,
      sourceMessage: row.sourceMessage,
      resultsAccepted: row.resultsAccepted,
      fieldAnnotations: row.fieldAnnotations,
      metadata,
   };
}

export function applyAktRestoreSnapshot<T extends RestorableRow>(
   row: T,
   snapshot: ServitutAktRestoreSnapshot,
): T {
   const documentSource = snapshot.documentSource === "rigsarkivetRequired"
      ? "rigsarkivetRequired"
      : "tingbog";
   return {
      ...row,
      handling: snapshot.handling,
      documentSource,
      electronicServitut: snapshot.electronicServitut,
      customPdf: undefined,
      aktRestore: undefined,
      annotationRecord: undefined,
      annotationDocument: undefined,
      aktDocument: snapshot.aktDocument,
      aktId: snapshot.aktId,
      documentUrl: snapshot.documentUrl,
      pdfStatus: snapshot.pdfStatus,
      pdfSource: snapshot.pdfSource,
      analysisItem: snapshot.analysisItem,
      selectedPages: snapshot.selectedPages,
      provenance: provenanceAfterPageRevert(snapshot.provenance),
      sourceMessage: snapshot.sourceMessage,
      resultsAccepted: false,
      fieldAnnotations: snapshot.fieldAnnotations,
   };
}

export function fallbackRestoreAfterCustomUpload<T extends RestorableRow>(row: T): T {
   const electronicServitut = row.electronicServitut
      || (looksLikeElectronicServitut(row.servitut) ? row.servitut : undefined);
   const handling: ServitutRedegoerelseRowHandling = electronicServitut && !row.aktId
      ? "electronicZipPackage"
      : "legacyLlmPdf";
   return {
      ...row,
      handling,
      documentSource: row.documentSource === "rigsarkivetRequired" ? "rigsarkivetRequired" : "tingbog",
      electronicServitut,
      customPdf: undefined,
      aktRestore: undefined,
      annotationRecord: undefined,
      annotationDocument: undefined,
      analysisItem: undefined,
      selectedPages: undefined,
      sourceMessage: undefined,
      resultsAccepted: false,
      pdfStatus: row.aktId || electronicServitut ? "ready" : "missing",
      aktDocument: electronicServitut
         ? Localization.getText("Digital land registry PDF")
         : row.aktDocument,
      provenance: provenanceAfterPageRevert(
         provenanceAfterDocumentChange(getRowProvenance(row), { source: "akt", origin: "ai" }),
      ),
   };
}

export function replaceRowMetadata(
   metadata: LLMMetadata[],
   rowKey: string,
   nextEntry?: LLMMetadata,
): LLMMetadata[] {
   const without = metadata.filter((entry) => entry.id !== rowKey);
   return nextEntry ? [...without, nextEntry] : without;
}

export function shouldRestoreAktAfterPageRevert(row: {
   aktRestore?: ServitutAktRestoreSnapshot;
   customPdf?: unknown;
}): boolean {
   return Boolean(row.aktRestore || row.customPdf);
}
